/* eslint-disable no-restricted-imports */
import React, { useEffect, useMemo } from 'react';
import Select, { components } from 'react-select';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import flowRight from 'lodash.flowright';
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core';

import { actions as reduxActions } from './reducers';
import styles from './styles';
import { getClientsList } from './actions';
import Optional from '../../utils/optional';
import createSelectClientOptions from './actions/createSelectClientOptions';
import { reactSelectDefaultStyles } from '../../consts/reactSelectDefaultStyles';
import history from '../../../history';
import { actions as dashboardReduxActions } from '../../pages/clientDashboard/reducers';
import { URLS } from '../../consts/endpoints';
import { getData } from '../../utils/http';
import {
  doesArrayHasLength,
  isDefined,
  isUndefined
} from '../../utils/isDefined';
import checkIfSimcardsShouldBeShown from '../../utils/checkIfSimcardsShouldBeShown';
import LastClients from './components/LastClients';
import {
  getCurrentValues,
  setSearchHistory
} from '../../utils/localstorageUtils';
import withSettings from '../../utils/withSettings';
import checkIfIoTShouldBeShown from '../../utils/checkIfIoTShouldBeShown';
import can from '../../utils/can';
import createSubject from '../../utils/createSubject';
import { translate } from '../../i18n/I18nProvider';
import { ROLES_IDS } from '../../consts/rolesIds';

const SelectClient = ({
  clientsList,
  selectedClientId,
  getClientsList,
  toggleDialog,
  classes,
  open,
  resetSelectClient,
  setSelectedClient,
  role,
  adminDomains,
  resetDashboard,
  setHistory,
  settings
}) => {
  useEffect(
    () => {
      if (
        (role.id >= ROLES_IDS.PARTNER_ADMIN ||
          doesArrayHasLength(adminDomains)) &&
        (isUndefined(clientsList) || !doesArrayHasLength(clientsList))
      ) {
        getClientsList({ role, adminDomains });
      }
    },
    // eslint-disable-next-line
    [, role, adminDomains, clientsList]
  );

  useEffect(() => {
    return resetSelectClient;
  }, [resetSelectClient]);

  useEffect(() => {
    const history = getCurrentValues({
      name: `clients`
    });

    setHistory(history);
  }, [clientsList, setHistory]);

  const options = useMemo(() => createSelectClientOptions(clientsList), [
    clientsList
  ]);

  const handleChange = async selected => {
    resetDashboard();

    const newSearchHistory = setSearchHistory({
      name: `clients`,
      value: selected.value
    });

    if (isDefined(newSearchHistory)) {
      setHistory(newSearchHistory);
    }

    history.push(`/${selected.value}/dashboard`);
    let selectedClient = clientsList.find(
      client => client.id === selected.value
    );

    toggleDialog();

    const hasCientSimcards = await checkIfSimcardsShouldBeShown(selected.value);

    let client;
    try {
      const { data } = await getData(
        `${URLS.CLIENTS}/${selected.value}.json?include=partner`
      );

      client = data;
    } finally {
      if (isDefined(client)) {
        selectedClient = client;
      }
    }

    let hasCientIoTSubs = false;

    setSelectedClient({
      ...selectedClient,
      hasCientSimcards,
      hasCientIoTSubs
    });

    if (
      doesArrayHasLength(selectedClient?.metadata?.account_ids) &&
      can(
        'read',
        createSubject('Quote::Subscription', {
          client_id: Number(selected.value)
        })
      )
    ) {
      hasCientIoTSubs = await checkIfIoTShouldBeShown(selected.value);

      setSelectedClient({
        ...selectedClient,
        hasCientSimcards,
        hasCientIoTSubs
      });
    }
  };

  const handleClose = () => {
    toggleDialog();
  };

  const Option = props => {
    const { children } = props;
    return (
      <components.Option {...props}>
        <div>
          <div>{Optional(children?.name).or('')}</div>
          <div className={classes.accountNo}>
            <div className={classes.platform}>
              {Optional(children?.platform).or('')}
            </div>
            {Optional(children?.notes).or('')}
          </div>
        </div>
      </components.Option>
    );
  };

  const customFilter = (option, searchText) => {
    if (
      option.data.label.name.toLowerCase().includes(searchText.toLowerCase()) ||
      option.data.label.notes.toLowerCase().includes(searchText.toLowerCase())
    ) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <Dialog
      classes={{
        paper: classes.dialogContent,
        scrollPaper: classes.scrollPaper
      }}
      open={open}
      onClose={handleClose}
    >
      <DialogTitle
        className={classes.dialogTitle}
        style={{
          backgroundColor: settings.colors.modal_header,
          color: settings.colors.modal_header_text
        }}
      >
        {translate('MENU.SWITCH_CLIENT')}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Select
          styles={reactSelectDefaultStyles}
          className={classes.select}
          value={selectedClientId}
          onChange={handleChange}
          autoFocus
          options={options.length > 0 ? options : ''}
          placeholder={`${translate('EDIT.SELECT_CLIENT')} ...`}
          components={{ Option }}
          filterOption={customFilter}
        />
        <LastClients handleChange={handleChange} />
      </DialogContent>
    </Dialog>
  );
};

const mapStatesToProps = ({ selectClient, auth }) => {
  return {
    clientsList: Optional(selectClient.selectClientsList).or([]),
    selectedClientId: Optional(selectClient.selectedClient)
      .map(client => client.id)
      .or(auth.defaultClient.id),
    open: selectClient.open,
    role: auth.role,
    adminDomains: auth.user.admin_domains
  };
};

const mapDispatchToProps = {
  getClientsList,
  toggleDialog: reduxActions.toggleDialog,
  resetSelectClient: reduxActions.resetSelectClient,
  setSelectedClient: reduxActions.setSelectedClient,
  resetDashboard: dashboardReduxActions.resetAdmins,
  setHistory: reduxActions.setHistory
};

export default flowRight(
  withSettings,
  withRouter,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(SelectClient);
